import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";

/**
 * FindYourStayCTA — reusable bottom-of-page call-to-action. Drop this onto
 * secondary pages (FAQ, About, Guest Love, Policies etc.) where conversion
 * otherwise dead-ends. Positions itself as a calm editorial banner rather
 * than a noisy popup.
 */
export default function FindYourStayCTA({
  eyebrow = "Ready when you are",
  title = "Find your next stay",
  subtitle,
  primaryTo = "/stays",
  primaryLabel = "Browse all stays",
  secondaryTo = "/contact",
  secondaryLabel = "Talk to our team",
  className = "",
}) {
  // Default subtitle pulls the live property count so removing or adding a
  // listing in admin/Hostify auto-refreshes the marketing copy.
  const [count, setCount] = useState(null);
  useEffect(() => {
    if (subtitle) return; // caller supplied one — don't fetch.
    api.get("/site-config").then(({ data }) => setCount(Number(data.active_properties_count) || null)).catch(() => {});
  }, [subtitle]);
  const computedSubtitle = subtitle ?? (count
    ? `${count} quiet homes across South Delhi and Gurugram — each one built, hosted and housekept by us.`
    : "Quiet homes across South Delhi and Gurugram — each one built, hosted and housekept by us.");
  return (
    <section data-testid="find-your-stay-cta" className={`mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 lg:py-20 ${className}`}>
      <p className="vyg-eyebrow">{eyebrow}</p>
      <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl">{title}</h2>
      {computedSubtitle && <p className="editorial-body mt-4 mx-auto max-w-xl !text-base text-noir/70">{computedSubtitle}</p>}
      <div className="mt-7 flex flex-wrap justify-center gap-3">
        <Link to={primaryTo} className="vyg-cta" data-testid="find-your-stay-primary">
          {primaryLabel} <ArrowUpRight size={14} />
        </Link>
        {secondaryTo && (
          <Link to={secondaryTo} className="inline-flex items-center gap-1.5 rounded-full border border-forest/20 px-5 py-2.5 text-sm text-forest hover:bg-mist/40" data-testid="find-your-stay-secondary">
            {secondaryLabel}
          </Link>
        )}
      </div>
    </section>
  );
}
